<template>
  <div :class="{ 'pagination-wrapper': showPagination, 'pagination-wrapper--no-pagination': !showPagination }">
    <ul
      style="grid-column-start: 2"
      v-if="showPagination"
      class="pagination-flat justify-content-center pagination pagination--on-foreground"
    >
      <li class="page-item prev" :class="{ 'disabled': intValue === 1 }">
        <a class="page-link" href="#" @click="goToPrevious">
          <arrow-left-icon/>
        </a>
      </li>

      <template v-if="intValue - sides > 1">
        <li class="page-item" key="first-page">
          <a class="page-link" href="#" @click="goToPage($event, 1)">
            1
          </a>
        </li>
        <li class="page-item" key="skipped-left-pages">
          <span class="page-link page-link--dots">&hellip;</span>
        </li>
      </template>

      <template v-for="(n, index) in range" :key="index">
        <li class="page-item" :class="{ 'active': n === intValue }">
          <a class="page-link" href="#" @click="goToPage($event, n)">{{ n }}</a>
        </li>
      </template>

      <template v-if="intValue + props.sides < props.config.last_page">
        <li class="page-item" key="skipped-right-pages">
          <span class="page-link page-link--dots">&hellip;</span>
        </li>
        <li class="page-item" key="last-page">
          <a class="page-link" href="#" @click="goToPage($event, props.config.last_page)">
            {{ props.config.last_page }}
          </a>
        </li>
      </template>

      <li class="page-item next" :class="{ 'disabled': intValue >= props.config.last_page }">
        <a class="page-link" href="#" @click="goToNext">
          <arrow-right-icon/>
        </a>
      </li>
    </ul>
    <div/>
    <div/>
    <div class="ml-auto" v-if="showPerPage">
      <div class="d-flex">
        <s-select
          class="bg-white"
          :model-value="perPage"
          @update:modelValue="onPerPageChanged"
          :items="itemsPerPageOptions"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import ArrowLeftIcon from '@/components/icons/arrow-left';
  import ArrowRightIcon from '@/components/icons/arrow-right';
  import { computed } from 'vue';

  const emit = defineEmits(['change', 'perPageChange']);

  const itemsPerPageOptions = [
    20, 50, 100,
  ];

  const props = defineProps({
    config: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number],
      required: true,
    },
    perPage: {
      default: 20,
      type: Number,
    },
    showPerPage: {
      type: Boolean,
      default: false,
    },
    // How many pages are visible from the sides
    sides: {
      type: Number,
      default: 2,
    },
    edge: {
      type: Boolean,
      default: false,
    },
  });

  const showPagination = computed(() => Object.keys(props.config).length !== 0 && props.config?.last_page && props.config?.last_page !== 1);

  // the page usually comes straight from the URL, so it can sit outside the real range
  // (?page=5000 on a 10-page list, or ?page=0). left as-is it feeds the range math a
  // negative remainder below and renders a link per page up to the bogus number
  const intValue = computed(() => {
    const page = Number(props.value);

    if (isNaN(page)) {
      return 0;
    }

    const lastPage = Number(props.config?.last_page);

    if (!lastPage) {
      return page;
    }

    return Math.min(Math.max(page, 1), lastPage);
  });

  const range = computed(() => {
    const maxVisibleSides = props.sides * 2;
    const maxVisible = maxVisibleSides + 1;

    // If total pages are less than maximum visible, show all
    if (props.config.last_page < maxVisible) {
      return getRange(1, props.config.last_page);
    }

    const pages = [];

    // Add pre-range of the pagination
    if (intValue.value + props.sides > props.config.last_page) {
      // Calculate how many items should be filled on the left to prevent whole page count shrink
      const fillableLeft = maxVisibleSides - (props.config.last_page - intValue.value);
      addRange(pages, Math.max(0, intValue.value - fillableLeft), intValue.value - 1);
    } else {
      addRange(pages, Math.max(1, intValue.value - props.sides), intValue.value - 1);
    }

    // Add current page
    pages.push(intValue.value);

    // Add post range
    if (intValue.value - props.sides <= 0) {
      // Calculate how many items should be filled on the right to prevent whole page count shrink
      const fillableRight = maxVisibleSides - intValue.value + 1;
      addRange(pages, intValue.value + 1, Math.min(intValue.value + fillableRight, props.config.last_page));
    } else {
      addRange(pages, intValue.value + 1, Math.min(intValue.value + props.sides, props.config.last_page));
    }

    return pages;
  });

  function goToPage(e, page) {
    e.preventDefault();

    if (page <= 0 || page > props.config.last_page || intValue.value === page) {
      return;
    }

    emit('change', page);
  }

  function goToPrevious(e) {
    e.preventDefault();

    if (intValue.value - 1 <= 0) {
      return;
    }

    goToPage(e, intValue.value - 1);
  }

  function goToNext(e) {
    e.preventDefault();

    if (intValue.value + 1 > props.config.last_page) {
      return;
    }

    goToPage(e, intValue.value + 1);
  }

  function addRange(arr, from, to) {
    for (let i = from; i <= to; i++) {
      arr.push(i);
    }
  }

  function getRange(from, to) {
    const entry = [];
    for (let i = from; i <= to; i++) {
      entry.push(i);
    }

    return entry;
  }

  function onPerPageChanged(value) {
    emit('perPageChange', value);
  }
</script>
